import { useEffect, useRef, useState, type ReactNode } from "react";
import type { MfaAuthRequest, MfaPhoneConfig } from "../../scenario/types";
import { audio } from "../../lib/audio";
import { activeTtsLang } from "../../lib/locale";
import { InfoRows, type MfitDone, type MfitGuideFn } from "../mfit/shared";
import { setDeskBuzz, vibrate } from "./sfx";
import "./mfa.css";
import { tx, txr } from "../../lib/tx";

/**
 * Alex's iPhone for "Approve or Deny".
 *  mode "auth": one or more of Alex's own sign-ins (number matching).
 *  mode "bomb": unexpected requests one after another (MFA bombing).
 *  mode "call": the fake IT call, then the SMS link, the fake identity page
 *               and a real Authenticator request with the attacker's number.
 * Every request uses the same notification sound: a real and an attacker
 * request look and sound the same, only the context tells them apart.
 */
type Stage =
  | "lock"
  | "request"
  | "denytip"
  | "report"
  | "between"
  | "summary"
  | "approved"
  | "ring"
  | "call"
  | "deciding"
  | "result"
  | "sms"
  | "page"
  | "inspect"
  | "trapauth"
  | "trapreport"
  | "frozen";

type BombLog = "reported" | "denied" | "tried" | "ignored" | "timeout";
const BOMB_LABEL: Record<BombLog, string> = {
  reported: "Denied and reported",
  denied: "Denied",
  tried: "You typed a number",
  ignored: "Dismissed",
  timeout: "Timed out",
};

interface Line {
  who: "them" | "me";
  text: string;
}

export function MfaPhone({ config, onDone, onGuideChange }: { config: MfaPhoneConfig; onDone: (d: MfitDone) => void; onGuideChange?: MfitGuideFn }) {
  const reqs = config.requests ?? [];
  const startStage: Stage =
    config.mode === "call" ? (config.replay || config.practice ? "page" : config.call?.outgoing ? "call" : "ring") : "lock";
  const [stage, setStage] = useState<Stage>(startStage);
  const [idx, setIdx] = useState(0);
  const [digits, setDigits] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [stuck, setStuck] = useState(false);
  const [left, setLeft] = useState<number | null>(null);
  const [log, setLog] = useState<BombLog[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [round, setRound] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const [result, setResult] = useState<{ title: string; body: string; eventId?: string } | null>(null);
  const [authBanner, setAuthBanner] = useState(false);
  const [zoom, setZoom] = useState(false);
  const events = useRef<string[]>([]);
  const doneRef = useRef(false);
  const alive = useRef(true);
  const voice = { voice: config.call?.voiceId || "ash", lang: activeTtsLang() };
  const req: MfaAuthRequest | undefined = reqs[idx];
  const ev = (k: keyof NonNullable<MfaPhoneConfig["events"]>) => {
    const id = config.events?.[k];
    if (id && !config.practice && !config.replay && !events.current.includes(id)) events.current.push(id);
  };
  const finish = (key: string) => {
    if (doneRef.current) return;
    doneRef.current = true;
    setDeskBuzz(false);
    audio.stopRing();
    onDone({ key, events: events.current.map((eventId) => ({ eventId })) });
  };

  // ---- guide text follows the stage
  const guideKey = config.replay && config.guide?.[`${stage}_replay`] ? `${stage}_replay` : stage;
  useEffect(() => {
    onGuideChange?.(config.guide?.[guideKey] ?? null);
  }, [guideKey, config.guide, onGuideChange]);
  useEffect(
    () => () => {
      alive.current = false;
      onGuideChange?.(null);
      audio.cancelSpeech();
      audio.stopRing();
      setDeskBuzz(false);
    },
    [onGuideChange],
  );

  // ---- spoken lines (Daniel): prefetched so the voice prerender records them
  useEffect(() => {
    const c = config.call;
    if (!c || config.replay || config.practice) return;
    const all = [...c.greeting, ...c.complyLines, ...c.rounds.flatMap((r) => r.options.flatMap((o) => o.reply ?? []))];
    all.forEach((t) => void audio.prefetchSpeech(t, voice));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const say = async (texts: string[]) => {
    setSpeaking(true);
    for (const t of texts) {
      if (!alive.current) return;
      setLines((cur) => [...cur, { who: "them", text: t }]);
      await audio.speak(t, voice);
    }
    if (alive.current) setSpeaking(false);
  };

  // ---- an arriving request: same sound and buzz for real and attacker requests
  const arrive = () => {
    audio.ui("alert");
    vibrate();
    setDeskBuzz(true);
    window.setTimeout(() => setDeskBuzz(false), 1800);
  };
  useEffect(() => {
    if (config.mode !== "call" && stage === "lock") arrive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, idx]);

  // ---- bombing: each request expires
  useEffect(() => {
    if (config.mode !== "bomb" || (stage !== "lock" && stage !== "request")) {
      setLeft(null);
      return;
    }
    setLeft((l) => (l == null ? req?.timeoutS ?? 20 : l));
    const t = window.setInterval(() => setLeft((l) => (l == null ? l : Math.max(0, l - 1))), 1000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, idx]);
  useEffect(() => {
    if (left === 0 && config.mode === "bomb") nextBomb("timeout");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [left]);

  // ---- own sign-in: a tip when the learner seems stuck
  useEffect(() => {
    if (config.mode !== "auth" || stage !== "request" || !config.stuckTip) return;
    const t = window.setTimeout(() => setStuck(true), 14000);
    return () => window.clearTimeout(t);
  }, [stage, config.mode, config.stuckTip]);

  const handled = useRef(-1);
  const nextBomb = (what: BombLog) => {
    if (handled.current === idx) return;
    handled.current = idx;
    setLog((cur) => [...cur, what]);
    setDigits("");
    setErr(null);
    setLeft(null);
    if (idx + 1 < reqs.length) {
      setStage("between");
      window.setTimeout(() => {
        if (!alive.current) return;
        setIdx((i) => i + 1);
        setStage("lock");
      }, 2400);
    } else setStage("summary");
  };

  const endBomb = () => {
    const has = (k: BombLog) => log.includes(k);
    if (has("reported")) ev("reported_bombing");
    else if (has("denied")) ev("denied_bombing");
    if (has("tried")) ev("tried_number");
    if (!has("reported") && !has("denied") && !has("tried")) ev("ignored_bombing");
    if (!has("reported")) ev("unreported");
    finish(has("tried") ? "attempted" : has("reported") ? "reported" : has("denied") ? "denied" : "ignored");
  };

  // ---- the Authenticator keypad + buttons
  const want = config.mode === "call" ? config.trap?.number ?? "" : req?.number ?? "";
  const len = Math.max(2, want.length || 2);
  const press = (k: string) => {
    audio.ui("key");
    setErr(null);
    if (k === "del") setDigits((d) => d.slice(0, -1));
    else setDigits((d) => (d.length >= len ? d : d + k));
  };
  const onYes = () => {
    if (digits.length < len) return;
    if (config.mode === "bomb") {
      audio.ui("error");
      setErr("No sign-in matches that number. Microsoft denied this request.");
      window.setTimeout(() => alive.current && nextBomb("tried"), 2200);
      return;
    }
    if (digits !== want) {
      audio.ui("error");
      setErr("That number doesn't match. Check the number on the sign-in screen.");
      setDigits("");
      return;
    }
    if (config.mode === "auth") {
      setStage("approved");
      audio.ui("success");
      window.setTimeout(() => {
        ev("approved_legit");
        finish("approved");
      }, 1700);
      return;
    }
    // call mode: Alex typed the attacker's number
    ev("approved_attacker");
    setStage("frozen");
    window.setTimeout(() => alive.current && setZoom(true), 1500);
    window.setTimeout(() => audio.breachAlert(), 2300);
    window.setTimeout(() => finish("approved"), 3700);
  };
  const onNo = () => {
    audio.click();
    setDigits("");
    setErr(null);
    if (config.mode === "auth") setStage("denytip");
    else if (config.mode === "bomb") {
      if (config.reportPrompt) setStage("report");
      else nextBomb("denied");
    }
    else rejectTrap();
  };
  const rejectTrap = () => {
    audio.callHangup();
    ev("trap_rejected");
    if (config.trap?.report && !config.practice) setStage("trapreport");
    else finish("rejected");
  };

  // ---- call
  const answer = () => {
    audio.stopRing();
    audio.click();
    setStage("call");
    void say(config.call?.greeting ?? []).then(() => alive.current && setStage("deciding"));
  };
  // outgoing call (the learner dialled the number): ringback, then the agent answers
  useEffect(() => {
    if (!config.call?.outgoing || config.replay || config.practice) return;
    audio.startRing();
    const t = window.setTimeout(() => {
      audio.stopRing();
      if (!alive.current) return;
      void say(config.call?.greeting ?? []).then(() => alive.current && setStage("deciding"));
    }, 2600);
    return () => {
      window.clearTimeout(t);
      audio.stopRing();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (stage !== "ring") return;
    audio.startRing();
    vibrate(0.1, 3);
    setDeskBuzz(true);
    const t = window.setInterval(() => vibrate(0.1, 2), 2600);
    return () => {
      window.clearInterval(t);
      setDeskBuzz(false);
    };
  }, [stage]);

  const pick = (o: NonNullable<MfaPhoneConfig["call"]>["rounds"][number]["options"][number]) => {
    audio.click();
    setLines((cur) => [...cur, { who: "me", text: o.label }]);
    if (o.eventId && !events.current.includes(o.eventId)) events.current.push(o.eventId);
    setStage("call");
    if (o.hangup) {
      void say(o.reply ?? []).then(() => {
        if (!alive.current) return;
        audio.callHangup();
        if (o.result) {
          setResult(o.result);
          setStage("result");
        } else finish("safe");
      });
      return;
    }
    if (o.comply) {
      ev("call_complied");
      void say([...(o.reply ?? []), ...(config.call?.complyLines ?? [])]).then(() => {
        if (!alive.current) return;
        if (config.call?.endOnComply) {
          finish("comply");
          return;
        }
        audio.ui("message");
        vibrate();
        setStage("sms");
      });
      return;
    }
    void say(o.reply ?? []).then(() => {
      if (!alive.current) return;
      setRound((r) => Math.min(r + 1, (config.call?.rounds.length ?? 1) - 1));
      setStage("deciding");
    });
  };

  // the real Authenticator request arrives while the fake page is open
  useEffect(() => {
    if (stage !== "page" || authBanner) return;
    const t = window.setTimeout(() => {
      if (!alive.current) return;
      setAuthBanner(true);
      arrive();
    }, 2600);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // ---------------------------------------------------------------- render
  const clock = config.clock ?? "09:05";
  const trap = config.trap;

  const authCard = (r: { app: string; account: string; location?: string }, bomb: boolean) => (
    <div className="mfx-auth">
      <div className="mfx-auth-top">
        <span className="mfx-auth-logo" aria-hidden="true" />
        <b>{tx("Authenticator")}</b>
      </div>
      <div className="mfx-auth-card">
        <div className="mfx-auth-q">{tx("Are you trying to sign in?")}</div>
        <div className="mfx-auth-acct">
          <b>{r.app}</b>
          <span>{r.account}</span>
        </div>
        {r.location && (
          <div className="mfx-auth-loc">
            <i aria-hidden="true" />
            {txr("Approximate location: {location}", { location: r.location })}
          </div>
        )}
        <div className="mfx-auth-hint">{tx("Enter the number shown to sign in.")}</div>
        <div className="mfx-digits" aria-label={tx("Number")}>
          {Array.from({ length: len }).map((_, i) => (
            <span key={i} className={digits[i] ? "is-on" : ""}>
              {digits[i] ?? ""}
            </span>
          ))}
        </div>
        {err && <div className="mfx-err">{err}</div>}
        {!err && stuck && config.stuckTip && <div className="mfx-tip">{config.stuckTip}</div>}
        {bomb && left != null && <div className="mfx-timer">{txr("This request expires in 0:{v}", { v: String(left).padStart(2, "0") })}</div>}
        <div className="mfx-auth-btns">
          {!config.replay && (
            <button type="button" className="mfx-btn mfx-btn--no" onClick={onNo}>
              {tx("No, it's not me")}
            </button>
          )}
          <button
            type="button"
            className={`mfx-btn mfx-btn--yes${digits.length >= len ? " cf-pulse-target" : ""}`}
            disabled={digits.length < len}
            onClick={onYes}
          >
            {tx("Yes")}
          </button>
        </div>
      </div>
      <div className="mfx-pad">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"].map((k) =>
          k === "" ? (
            <span key="sp" />
          ) : (
            <button key={k} type="button" className="mfx-key" onClick={() => press(k)} aria-label={k === "del" ? tx("Delete") : k}>
              {k === "del" ? "⌫" : k}
            </button>
          ),
        )}
      </div>
    </div>
  );

  let screen: ReactNode = null;

  if (stage === "lock" || stage === "between") {
    screen = (
      <div className="mfx-lock">
        <div className="mfx-lock-time">{clock}</div>
        <div className="mfx-lock-date">{txr("{ownerName}'s iPhone", { ownerName: config.ownerName })}</div>
        {stage === "lock" && req && (
          <div className="mfx-notif-wrap">
            <button type="button" className="mfx-notif cf-pulse-target" onClick={() => setStage("request")}>
              <span className="mfx-auth-logo" aria-hidden="true" />
              <span className="mfx-notif-t">
                <b>{tx("Authenticator")}</b>
                <span>{txr("Approve sign-in? {account}", { account: req.account })}</span>
              </span>
              <span className="mfx-notif-time">{tx("now")}</span>
            </button>
            {config.mode === "bomb" && (
              <button type="button" className="mfx-dismiss" onClick={() => nextBomb("ignored")}>
                {tx("Dismiss")}
              </button>
            )}
          </div>
        )}
        {stage === "between" && (
          <div className="mfx-lock-note">
            {txr("{length} of {length2} · {v}", { length: log.length, length2: reqs.length, v: BOMB_LABEL[log[log.length - 1] ?? "denied"] })}
          </div>
        )}
      </div>
    );
  } else if (stage === "request" && req) {
    screen = authCard(req, config.mode === "bomb");
  } else if (stage === "approved") {
    screen = (
      <div className="mfx-done">
        <span className="mfx-done-ico" aria-hidden="true">✓</span>
        <b>{tx("Approved")}</b>
        <span>{req?.app}</span>
      </div>
    );
  } else if (stage === "denytip" && config.denyTip) {
    screen = (
      <div className="mfx-panel">
        <div className="mfx-panel-t">{config.denyTip.title}</div>
        <p>{config.denyTip.body}</p>
        <button
          type="button"
          className="mfx-btn mfx-btn--yes cf-pulse-target"
          onClick={() => {
            setStage("lock");
          }}
        >
          {config.denyTip.retryLabel}
        </button>
      </div>
    );
  } else if (stage === "report" && config.reportPrompt) {
    const rp = config.reportPrompt;
    screen = (
      <div className="mfx-panel">
        <span className="mfx-auth-logo is-big" aria-hidden="true" />
        <div className="mfx-panel-t">{rp.title}</div>
        <p>{rp.body}</p>
        <button type="button" className="mfx-btn mfx-btn--yes" onClick={() => {
            audio.ui("success");
            nextBomb("reported");
          }}>
          {rp.reportLabel}
        </button>
        <button type="button" className="mfx-btn mfx-btn--ghost" onClick={() => nextBomb("denied")}>
          {rp.skipLabel}
        </button>
      </div>
    );
  } else if (stage === "summary") {
    screen = (
      <div className="mfx-panel">
        <div className="mfx-panel-k">{tx("Authenticator · last 2 minutes")}</div>
        <div className="mfx-panel-t">{txr("{length} sign-in requests you did not start", { length: reqs.length })}</div>
        <ul className="mfx-sum">
          {log.map((l, i) => (
            <li key={i} className={`is-${l}`}>
              <b>
                {txr("{v} · request {v2}", { v: reqs[i]?.location ?? tx("Unknown location"), v2: i + 1 })}
              </b>
              <span>{BOMB_LABEL[l]}</span>
            </li>
          ))}
        </ul>
        <button type="button" className="mfx-btn mfx-btn--yes cf-pulse-target" onClick={endBomb}>
          {tx("Continue")}
        </button>
      </div>
    );
  } else if (stage === "ring" && config.call) {
    screen = (
      <div className="mfx-ring">
        <div className="mfx-ring-k">{tx("incoming call")}</div>
        <div className="mfx-ring-n">{config.call.callerName}</div>
        <div className="mfx-ring-s">{config.call.callerSubtitle ?? tx("mobile")}</div>
        <div className="mfx-ring-av" aria-hidden="true">
          {config.call.callerName.slice(0, 2).toUpperCase()}
        </div>
        <button type="button" className="mfx-answer cf-pulse-target" onClick={answer} aria-label={tx("Answer")}>
          <span aria-hidden="true">📞</span>
          {tx("Answer")}
        </button>
      </div>
    );
  } else if ((stage === "call" || stage === "deciding" || stage === "result" || stage === "sms") && config.call) {
    const opts = config.call.rounds[round]?.options ?? [];
    screen = (
      <div className="mfx-call">
        <div className="mfx-call-h">
          <span className="mfx-call-av" aria-hidden="true">
            {config.call.callerName.slice(0, 2).toUpperCase()}
          </span>
          <span>
            <b>{config.call.callerName}</b>
            <small className={speaking ? "is-live" : ""}>{speaking ? tx("speaking…") : stage === "result" ? tx("call ended") : config.call?.outgoing && lines.length === 0 ? tx("calling…") : tx("on call")}</small>
          </span>
        </div>
        <Transcript lines={lines} />
        {stage === "deciding" && (
          <div className="mfx-opts">
            {config.call.rounds[round]?.prompt && <div className="mfx-opts-q">{config.call.rounds[round]?.prompt}</div>}
            {opts.map((o, i) => (
              <button key={o.id} type="button" className="mfx-opt" style={{ animationDelay: `${i * 0.9}s` }} onClick={() => pick(o)}>
                {o.label}
              </button>
            ))}
          </div>
        )}
        {stage === "result" && result && (
          <div className="mfx-panel is-inline">
            <div className="mfx-panel-t">{result.title}</div>
            <p>{result.body}</p>
            <button type="button" className="mfx-btn mfx-btn--yes cf-pulse-target" onClick={() => finish("safe")}>
              {tx("Continue")}
            </button>
          </div>
        )}
        {stage === "sms" && trap && (
          <button type="button" className="mfx-notif is-top cf-pulse-target" onClick={() => setStage("page")}>
            <span className="mfx-sms-logo" aria-hidden="true" />
            <span className="mfx-notif-t">
              <b>{txr("Messages · {smsFrom}", { smsFrom: trap.smsFrom })}</b>
              <span>{trap.smsText}</span>
            </span>
            <span className="mfx-notif-time">{tx("now")}</span>
          </button>
        )}
      </div>
    );
  } else if ((stage === "page" || stage === "inspect") && trap) {
    screen = (
      <div className="mfx-safari">
        <button
          type="button"
          className="mfx-safari-url"
          onClick={() => {
            ev("trap_inspected");
            setStage(stage === "inspect" ? "page" : "inspect");
          }}
        >
          <i aria-hidden="true">🔒</i>
          {trap.url.replace(/^https?:\/\//, "").split("/")[0]}
        </button>
        {stage === "inspect" ? (
          <div className="mfx-panel is-inline">
            <div className="mfx-panel-t">{trap.inspect.title}</div>
            <InfoRows rows={trap.inspect.rows} />
            {trap.inspect.note && <p>{trap.inspect.note}</p>}
            <button type="button" className="mfx-btn mfx-btn--ghost" onClick={() => setStage("page")}>
              {tx("Back to the page")}
            </button>
          </div>
        ) : (
          <div className="mfx-idpage">
            <div className="mfx-idpage-brand">
              <span className="mfx-ms" aria-hidden="true">
                <i />
                <i />
                <i />
                <i />
              </span>
              {tx("NorthBay IT")}
            </div>
            <div className="mfx-idpage-k">{trap.pageKicker}</div>
            <div className="mfx-idpage-t">{trap.pageTitle}</div>
            <p>{trap.pageBody}</p>
            <div className="mfx-idpage-num">{trap.number}</div>
            <div className="mfx-idpage-wait">
              <span className="mfx-spin" aria-hidden="true" />
              {tx("Waiting for verification…")}
            </div>
            <div className="mfx-idpage-sim">{tx("Training simulation: nothing you type here leaves this course.")}</div>
            {!config.replay && (
              <button type="button" className="mfx-btn mfx-btn--ghost" onClick={rejectTrap}>
                {trap.closeLabel}
              </button>
            )}
          </div>
        )}
        {authBanner && stage === "page" && (
          <button type="button" className="mfx-notif is-top cf-pulse-target" onClick={() => setStage("trapauth")}>
            <span className="mfx-auth-logo" aria-hidden="true" />
            <span className="mfx-notif-t">
              <b>{tx("Authenticator")}</b>
              <span>{txr("Approve sign-in? {v}", { v: config.requests?.[0]?.account ?? "" })}</span>
            </span>
            <span className="mfx-notif-time">{tx("now")}</span>
          </button>
        )}
      </div>
    );
  } else if ((stage === "trapauth" || stage === "frozen") && trap) {
    screen = (
      <div className={`mfx-frozen-wrap${stage === "frozen" ? " is-frozen" : ""}`}>
        {authCard({ app: "Microsoft 365", account: config.requests?.[0]?.account ?? "", location: trap.location }, false)}
        {stage === "frozen" && <div className="mfx-frozen-veil">{tx("Approved")}</div>}
      </div>
    );
  } else if (stage === "trapreport" && trap?.report) {
    const rp = trap.report;
    screen = (
      <div className="mfx-panel">
        <div className="mfx-panel-t">{rp.title}</div>
        <p>{rp.body}</p>
        <button
          type="button"
          className="mfx-btn mfx-btn--yes"
          onClick={() => {
            audio.ui("success");
            ev("trap_reported");
            finish("rejected");
          }}
        >
          {rp.reportLabel}
        </button>
        <button type="button" className="mfx-btn mfx-btn--ghost" onClick={() => finish("rejected")}>
          {rp.skipLabel}
        </button>
      </div>
    );
  }

  const light = ["request", "approved", "page", "inspect", "trapauth", "frozen"].includes(stage);
  return (
    <div className={`mfx-phone-stage${zoom ? " is-zooming" : ""}`}>
      <div className={`mfx-iphone${light ? " is-light" : ""}`}>
        <div className="mfx-iphone-bar" aria-hidden="true">
          <span>{clock}</span>
          <i className="mfx-island" />
          <span className="mfx-iphone-bar-r">{tx("5G ▮▮▮")}</span>
        </div>
        <div className="mfx-iphone-screen">{screen}</div>
      </div>
    </div>
  );
}

function Transcript({ lines }: { lines: Line[] }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollTo({ top: ref.current.scrollHeight, behavior: "smooth" });
  }, [lines.length]);
  return (
    <div className="mfx-tx" ref={ref}>
      {lines.map((l, i) => (
        <div key={i} className={`mfx-tx-l is-${l.who}`}>
          {l.text}
        </div>
      ))}
    </div>
  );
}
